/** @jsx jsx */
import React, { useCallback, useEffect, type SyntheticEvent, useState } from 'react';
import { jsx, css } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useMutation } from 'react-relay';
import { useIssueAri } from '@jira/issue__parse-issue-url/src/use-issue-ari/index.tsx';
import type { ApplicationEdition } from '@jira/platform__application-editions/src/edition.tsx';
import { token } from '@atlaskit/tokens';

import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import { Label } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/core/cross';
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	type KeyboardOrMouseEvent,
} from '@atlaskit/modal-dialog';
import { Text, Flex, Box, xcss, Grid } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { FeatureFlagValue } from '@atlassian/jira-feature-flagging-using-meta';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { MountEvent } from '@atlassian/analytics-bridge';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import ErrorBoundary, {
	type ExtraErrorAttributes,
} from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import { useArchiveIssueUrl } from '@atlassian/jira-issue-archival-archives/src/controllers/useArchiveIssueUrl.tsx';
import { useArchiveIssue } from '@atlassian/jira-issue-archival-modal/src/services/index.tsx';
import { isIssueViewPage } from '@atlassian/jira-issue-archival-modal/src/utils.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	MODAL,
	fireOperationalAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import {
	useProjectKey,
	useApplication,
	useEdition,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import type { Application } from '@atlassian/jira-shared-types/src/application.tsx';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import { useIssueTypeName } from '@atlassian/jira-issue-context-service/src/context.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { ISSUE_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIssueExperienceMonitoring } from '@atlassian/jira-issue-reliability-fetch/src/use-issue-experience-monitoring.tsx';
import type { deleteIssueView_deleteIssue_Mutation } from './__generated__/deleteIssueView_deleteIssue_Mutation.graphql';
import type { deleteIssueView_ArchiveIssueMutation } from './__generated__/deleteIssueView_ArchiveIssueMutation.graphql';
import messages from './messages.tsx';
import type { IsTriggeredFromNINView, IssueDeleteCallbackArgs } from './types.tsx';
import { useIsDataRetentionEnabled } from './useIsDataRetentionEnabled.tsx';
import { useIsDeleteIssueExperimentEnabled } from './useIsDeleteIssueExperimentEnabled.tsx';
import { useIsTwoStepIssueDeleteFreemiumExperimentEnabled } from './useIsTwoStepIssueDeleteFreemiumExperimentEnabled.tsx';
import { DELETE_ISSUE_TEXTFIELD_TEST_ID } from './selectors.tsx';

type IssueId = string;

const isTriggeredFromNINViewDefaults: IsTriggeredFromNINView = {
	isIssueArchivalEnabled: false,
	isTriggeredFromNINView: false,
};
const LOG_LOCATION = 'issue.delete.issue-delete-epic';
/** Custom hook for archive issue mutation */
export const useArchiveIssueWithRelay = () => {
	const cloudId = useCloudId();
	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commitArchiveIssueMutation] = useMutation<deleteIssueView_ArchiveIssueMutation>(graphql`
		mutation deleteIssueView_ArchiveIssueMutation($input: JiraIssueArchiveInput!) {
			jira_archiveIssue(input: $input) @optIn(to: "JiraIssueViewRelayMigrations") {
				success
				errors {
					message
				}
			}
		}
	`);

	const archiveIssue = useCallback(
		(issueID: string, onSuccess: () => void, onError: () => void): void => {
			commitArchiveIssueMutation({
				variables: {
					input: {
						ids: [
							createAri({
								resourceOwner: 'jira',
								cloudId,
								resourceType: 'issue',
								resourceId: issueID,
							}),
						],
					},
				},
				onCompleted: (response: deleteIssueView_ArchiveIssueMutation['response']) => {
					if (response?.jira_archiveIssue?.success) {
						onSuccess();
					} else {
						onError();
					}
				},
				onError: () => onError(),
			});
		},
		[commitArchiveIssueMutation, cloudId],
	);

	return { archiveIssue };
};

/** New custom hook for mutation
 * TODO: Shift to using local state objects instead of calling existing actions from Redux.
 * Currently, only DELETE_ISSUE_REQUEST redux action has been made obsolete.
 * See /issue/issue-view-store/src/reducers/issue-delete-reducer.tsx for the state variables that can be pulled in as local state objects
 * Code flow : https://hello.atlassian.net/wiki/spaces/JIE/whiteboard/5677608177?atl_f=PAGETREE
 * */
const useDeleteIssueWithRelay = () => {
	const { enrichCommitWithIssueExperienceMonitoring } = useIssueExperienceMonitoring();
	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit, isMutationInFlight] = useMutation<deleteIssueView_deleteIssue_Mutation>(graphql`
		mutation deleteIssueView_deleteIssue_Mutation($input: JiraIssueDeleteInput!) {
			jira_deleteIssue(input: $input) {
				success
				errors {
					message
					extensions {
						errorType
						statusCode
					}
				}
			}
		}
	`);
	const currentIssueARI = useIssueAri();
	const deleteIssue = useCallback(
		async (
			issueId: string, // TODO
			issueAri: string,
			issueKey: string,
			analyticsEvent: UIAnalyticsEvent,
			application: Application | undefined,
			edition: ApplicationEdition | undefined,
			triggerCloseDeleteIssueModal: () => void,
			onReduxDeleteSuccess: (issueKey: string) => void,
			onReduxDeleteFailure: (issueKey: string, error: Error) => void,
			onIssueDeleteSuccess?: ((args: IssueDeleteCallbackArgs) => void) | undefined,
			onClose?: () => void,
		) => {
			const issueARI = fg('timline_delete_issue') ? issueAri || currentIssueARI : currentIssueARI;

			commit(
				enrichCommitWithIssueExperienceMonitoring(
					{
						variables: {
							input: { id: issueARI, deleteSubtasks: true },
						},
						onCompleted: () => {
							fireTrackAnalytics(analyticsEvent, 'deleted issueDeleted');
							// Resume Redux flow
							onReduxDeleteSuccess(issueKey);
							triggerCloseDeleteIssueModal();

							// Execute post redux actions
							onIssueDeleteSuccess?.({ issueId, issueKey });

							onClose?.();
						},
						onError: (error: Error | FetchError) => {
							const additionalAttributes: Record<
								string,
								string | boolean | number | Record<string, FeatureFlagValue>
							> = {};
							if (error.message) {
								additionalAttributes.errorMessage = error.message;
							}

							if (error instanceof FetchError) {
								if (error.traceId) additionalAttributes.traceId = error.traceId;
								if (error.statusCode) additionalAttributes.statusCode = error.statusCode;
							}

							onReduxDeleteFailure(issueKey, error);
							log.safeErrorWithoutCustomerData(LOG_LOCATION, 'Failed to delete issue', error);
							fireOperationalAnalytics(analyticsEvent, 'delete failed', 'issueDeletedError', error);
						},
					},
					'DeleteIssueView_deleteIssue_Mutation',
					{
						overrideAttributesCallback: (isSuccess, mandatoryAttributes) => {
							const taskOverride = isSuccess
								? { task: 'deleteIssueSucceeded', action: 'taskSuccess' }
								: { task: 'deleteIssueFailed', action: 'taskFail' };

							return {
								...taskOverride,
								additionalAttributes: {
									...mandatoryAttributes,
									frameworkType: 'relay',
									// This seems to be reported incorrectly as the current page by useIssueExperienceMonitoring
									analyticsSource: 'issueDeleted',
									application: application ?? 'unknown',
									edition: edition ?? 'unknown',
								},
							};
						},
					},
				),
			);
		},
		[commit, currentIssueARI, enrichCommitWithIssueExperienceMonitoring],
	);

	return { deleteIssue, isDeleting: isMutationInFlight };
};

export type DeleteIssueViewProps = {
	issueId: IssueId;
	issueAri?: string;
	isDeleteIssueModalOpen: boolean;
	isDeletingIssue: boolean;
	issueKey: string;
	isSimplifiedProject: boolean;
	shouldShowChildSpecificCopy: boolean;
	didIssueDeleteFail: boolean;
	// eslint-disable-next-line jira/react/handler-naming
	triggerDeleteRequest: (
		issueKey?: string,
		analytics?: UIAnalyticsEvent,
		application?: Application | undefined,
		edition?: ApplicationEdition | undefined,
		deleteReq?: Promise<void> | false,
	) => void;
	onReduxDeleteSuccess: (issueKey: string) => void;
	onReduxDeleteFailure: (issueKey: string, error: Error) => void;
	// eslint-disable-next-line jira/react/handler-naming
	triggerCloseDeleteIssueModal: () => void;
	// eslint-disable-next-line jira/react/handler-naming
	deleteCancelAnalytics?: (arg1: UIAnalyticsEvent) => void;
	// eslint-disable-next-line jira/react/handler-naming
	deleteConfirmAnalytics?: (arg1: UIAnalyticsEvent) => void;
	onIssueDeleteSuccess?: ((arg1: IssueDeleteCallbackArgs) => void) | undefined;
	onIssueDeleteFailure?: ((arg1: IssueDeleteCallbackArgs) => void) | undefined;
	onClose?: () => void;
	workTypeKey?: string | null;
	isIssueArchivalEnabled?: boolean;
	isTriggeredFromNINView?: IsTriggeredFromNINView;
};

const DeleteIssueViewWithErrorBoundaryOld = AnalyticsSource(
	'issueDelete',
	MODAL,
)(({
	issueId,
	issueAri,
	isDeleteIssueModalOpen,
	isDeletingIssue,
	issueKey,
	isSimplifiedProject,
	shouldShowChildSpecificCopy,
	didIssueDeleteFail,
	triggerDeleteRequest,
	triggerCloseDeleteIssueModal,
	onReduxDeleteSuccess,
	onReduxDeleteFailure,
	deleteCancelAnalytics,
	deleteConfirmAnalytics,
	onIssueDeleteSuccess,
	onIssueDeleteFailure,
	onClose,
}: DeleteIssueViewProps) => {
	if (!isDeleteIssueModalOpen) return null;

	const { formatMessage } = useIntl();
	const previousIsDeletingIssue = usePrevious(isDeletingIssue);
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);
	const [workType] = useIssueTypeName();
	const { getRetentionDeleteButtonText, getRetentionDeleteHeading, getRetentionDeleteWarning } =
		useIsDataRetentionEnabled();

	useEffect(() => {
		const didDeletionFinish = !isDeletingIssue && previousIsDeletingIssue;

		if (didDeletionFinish) {
			if (!didIssueDeleteFail) {
				triggerCloseDeleteIssueModal();
				onIssueDeleteSuccess?.({
					issueId,
					issueKey,
				});
				onClose?.();
			} else {
				onIssueDeleteFailure?.({
					issueId,
					issueKey,
				});
			}
		}
	}, [
		didIssueDeleteFail,
		isDeletingIssue,
		issueId,
		issueKey,
		onClose,
		onIssueDeleteFailure,
		onIssueDeleteSuccess,
		previousIsDeletingIssue,
		triggerCloseDeleteIssueModal,
	]);

	const { deleteIssue } = useDeleteIssueWithRelay();
	const onConfirmDeleteIssue = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			deleteConfirmAnalytics?.(analyticsEvent);
			if (fg('relay-migration-issue-view-delete-issue')) {
				deleteIssue(
					issueId,
					issueAri || '',
					issueKey,
					analyticsEvent,
					application,
					edition,
					triggerCloseDeleteIssueModal,
					onReduxDeleteSuccess,
					onReduxDeleteFailure,
					onIssueDeleteSuccess,
					onClose,
				);
			}
			triggerDeleteRequest(issueKey, analyticsEvent, application, edition);
		},
		[
			deleteConfirmAnalytics,
			deleteIssue,
			issueId,
			issueAri,
			issueKey,
			application,
			edition,
			triggerCloseDeleteIssueModal,
			onReduxDeleteSuccess,
			onReduxDeleteFailure,
			onIssueDeleteSuccess,
			onClose,
			triggerDeleteRequest,
		],
	);

	const onCancel = (event: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		deleteCancelAnalytics?.(analyticsEvent);
		triggerCloseDeleteIssueModal();
	};

	const getChildConditionalDeleteHeading = () => {
		const dataRetentionHeading = getRetentionDeleteHeading(false);
		if (dataRetentionHeading) {
			return dataRetentionHeading;
		}

		if (workType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
			return messages.deleteHeadingIssueTerminologyRefreshM4;
		}

		const deleteWithChildrenHeadingMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.deleteWithChildrenHeadingIssueTermRefresh
			: messages.deleteWithChildrenHeading;

		if (shouldShowChildSpecificCopy) {
			return isSimplifiedProject
				? deleteWithChildrenHeadingMessage
				: messages.deleteWithSubtasksHeading;
		}
		return messages.deleteHeading;
	};

	const getDeleteWithChildrenWarningMessage = () => {
		if (workType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
			return messages.deleteWithChildrenWarningIssueTerminologyRefreshM4;
		}
		if (fg('jira-issue-terminology-refresh-m3')) {
			return messages.deleteWithChildrenWarningIssueTermRefresh;
		}
		return messages.deleteWithChildrenWarning;
	};

	const getDeleteWithSubtasksWarningMessage = () => {
		if (workType && expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
			return messages.deleteWithSubtasksWarningIssueTerminologyRefreshM4;
		}
		if (fg('jira-issue-terminology-refresh-m3')) {
			return messages.deleteWithSubtasksWarningIssueTermRefresh;
		}
		return messages.deleteWithSubtasksWarning;
	};

	const getChildConditionalDeleteWarning = () => {
		if (shouldShowChildSpecificCopy) {
			return isSimplifiedProject
				? getDeleteWithChildrenWarningMessage()
				: getDeleteWithSubtasksWarningMessage();
		}
		return fg('jira-issue-terminology-refresh-m3')
			? messages.deleteWarningIssueTermRefresh
			: messages.deleteWarning;
	};

	const cancelButton = (
		<Button
			appearance="subtle"
			onClick={onCancel}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="issue-view-foundation.issue-actions.delete-issue.cancel-button"
			isDisabled={isDeletingIssue}
			key="cancelButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.cancel-button.button"
		>
			{formatMessage(messages.cancel)}
		</Button>
	);

	const deleteButton = (
		<Button
			appearance="danger"
			onClick={onConfirmDeleteIssue}
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.foundation.issue-actions.delete-issue.confirm-button"
			disabled={isDeletingIssue}
			// @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'ReactChild | undefined'.
			iconAfter={isDeletingIssue ? <Spinner size="small" /> : null}
			key="deleteButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.confirm-button.button1"
		>
			{getRetentionDeleteButtonText() || formatMessage(messages.delete)}
		</Button>
	);

	return (
		<>
			<ViewTracker />
			<ShortcutScope>
				<ModalDialog
					// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
					messageId="issue-view-foundation.issue-actions.delete-issue.modal-dialog"
					messageType="transactional"
					width="small"
					onClose={onCancel}
				>
					<ModalHeader hasCloseButton={fg('issue-view-enghealth-40720-modal-close-button')}>
						<ModalTitle appearance="danger">
							{formatMessage(getChildConditionalDeleteHeading(), {
								issueKey,
								workType,
							})}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Text as="p">
							{getRetentionDeleteWarning(workType) ||
								formatMessage(getChildConditionalDeleteWarning(), { workType })}
						</Text>
						<Text as="p">
							{!expVal('jira-itr-work-type-exp-1', 'isEnabled', false) &&
								formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.deleteRecommendationIssueTermRefresh
										: messages.deleteRecommendation,
								)}
						</Text>
					</ModalBody>
					<ModalFooter>
						{cancelButton}
						{deleteButton}
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
			<MountEvent
				onMount={() => {
					sendExperienceAnalytics({
						experience: 'issueViewDeleteIssueModalRender',
						wasExperienceSuccesful: true,
						analyticsSource: 'issueViewIssueActionsDeleteIssue',
						application: application ?? null,
						edition: edition ?? null,
					});
				}}
			/>
		</>
	);
});

const DeleteIssueViewWithErrorBoundaryNew = AnalyticsSource(
	'issueDelete',
	MODAL,
)(({
	issueId,
	issueAri,
	isDeleteIssueModalOpen,
	isDeletingIssue,
	issueKey,
	isSimplifiedProject,
	shouldShowChildSpecificCopy,
	didIssueDeleteFail,
	triggerDeleteRequest,
	triggerCloseDeleteIssueModal,
	onReduxDeleteSuccess,
	onReduxDeleteFailure,
	deleteCancelAnalytics,
	deleteConfirmAnalytics,
	onIssueDeleteSuccess,
	onIssueDeleteFailure,
	onClose,
	workTypeKey,
	isIssueArchivalEnabled = false,
	isTriggeredFromNINView = isTriggeredFromNINViewDefaults,
}: DeleteIssueViewProps) => {
	if (!isDeleteIssueModalOpen) return null;

	const { formatMessage } = useIntl();
	const previousIsDeletingIssue = usePrevious(isDeletingIssue);
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);

	const [inputValue, setInputValue] = useState('');
	const { archiveIssue } = useArchiveIssue();
	const { archiveIssue: archiveIssueWithRelay } = useArchiveIssueWithRelay();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isIssueArchivalFromNINViewEnabled =
		(fg('additional-check-for-trigger-from-nin-view')
			? isTriggeredFromNINView.isIssueArchivalEnabled
			: isIssueArchivalEnabled) &&
		expVal(
			'issue_deletion_two_step_process_for_free_standard',
			'isIssueDeleteExperimentFreemiumEnabled',
			false,
		);
	const isDeleteIssueExperimentEnabled = useIsDeleteIssueExperimentEnabled();

	const isEligibleForIssueArchival = fg('additional-check-for-trigger-from-nin-view')
		? (!isTriggeredFromNINView?.isTriggeredFromNINView && isDeleteIssueExperimentEnabled) ||
			isIssueArchivalFromNINViewEnabled
		: isDeleteIssueExperimentEnabled || isIssueArchivalFromNINViewEnabled;

	const isTwoStepIssueDeleteFreemiumExperimentEnabled =
		fg('issue_deletion_two_step_process_free_standard') &&
		useIsTwoStepIssueDeleteFreemiumExperimentEnabled({
			isIssueArchivalEnabled: isEligibleForIssueArchival,
		});
	const { showFlag } = useFlagService();
	const archiveIssueUrl = useArchiveIssueUrl();
	const [issueType] = useFieldValue({ issueKey, fieldKey: ISSUE_TYPE });
	const workType = issueType?.name || workTypeKey;
	const { getRetentionDeleteButtonText, getRetentionDeleteHeading, getRetentionDeleteWarning } =
		useIsDataRetentionEnabled();

	useEffect(() => {
		const didDeletionFinish = !isDeletingIssue && previousIsDeletingIssue;

		if (didDeletionFinish) {
			if (!didIssueDeleteFail) {
				triggerCloseDeleteIssueModal();
				onIssueDeleteSuccess?.({
					issueId,
					issueKey,
				});
				onClose?.();
			} else {
				onIssueDeleteFailure?.({
					issueId,
					issueKey,
				});
			}
		}
	}, [
		didIssueDeleteFail,
		isDeletingIssue,
		issueId,
		issueKey,
		onClose,
		onIssueDeleteFailure,
		onIssueDeleteSuccess,
		previousIsDeletingIssue,
		triggerCloseDeleteIssueModal,
	]);

	const { deleteIssue } = useDeleteIssueWithRelay();
	const onConfirmDeleteIssue = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			deleteConfirmAnalytics?.(analyticsEvent);
			if (fg('relay-migration-issue-view-delete-issue')) {
				deleteIssue(
					issueId,
					issueAri || '',
					issueKey,
					analyticsEvent,
					application,
					edition,
					triggerCloseDeleteIssueModal,
					onReduxDeleteSuccess,
					onReduxDeleteFailure,
					onIssueDeleteSuccess,
					onClose,
				);
			}
			triggerDeleteRequest(issueKey, analyticsEvent, application, edition);
		},
		[
			application,
			deleteConfirmAnalytics,
			deleteIssue,
			edition,
			issueAri,
			issueId,
			issueKey,
			onClose,
			onIssueDeleteSuccess,
			onReduxDeleteFailure,
			onReduxDeleteSuccess,
			triggerCloseDeleteIssueModal,
			triggerDeleteRequest,
		],
	);

	const onCancel = (event: KeyboardOrMouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		fg('issue_deletion_two_step_process_free_standard') &&
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'CancelButton');
		deleteCancelAnalytics?.(analyticsEvent);
		triggerCloseDeleteIssueModal();
	};

	const onArchivalFailure = () => {
		showFlag({
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId:
				'issue-view-foundation.issue-actions.delete-issue.show-flag.error.issue-archival-error',
			messageType: 'transactional',
			type: 'error',
			title: formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.issueArchivalErrorFlagTitleIssueTermRefresh
					: messages.issueArchivalErrorFlagTitle,
				{
					issueKey,
				},
			),
			testId: 'jira-issue-archive.modal.error-flag',
		});
		fireTrackAnalytics(
			createAnalyticsEvent({
				action: 'archived',
				actionSubject: 'issue',
				attributes: { triggerPointKey: 'DeleteIssueModal' },
			}),
			'archiveIssue Failed',
		);
		triggerCloseDeleteIssueModal();
	};

	const onArchivalSuccess = () => {
		fireTrackAnalytics(
			createAnalyticsEvent({
				action: 'archived',
				actionSubject: 'issue',
				attributes: { triggerPointKey: 'DeleteIssueModal' },
			}),
			'archiveIssue Successful',
		);

		if (isIssueViewPage()) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.reload();
		}

		showFlag({
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId:
				'issue-view-foundation.issue-actions.delete-issue.show-flag.success.issue-archival-success',
			messageType: 'transactional',
			type: 'success',
			title: formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.issueArchivalSuccessFlagTitleIssueTermRefresh
					: messages.issueArchivalSuccessFlagTitle,
				{
					issueKey,
				},
			),
			testId: 'jira-issue-archive.modal.success-flag',
			actions: [
				{
					content: fg('jira-issue-terminology-refresh-m3')
						? messages.issueArchivalSuccessFlagActionViewIssueIssueTermRefresh
						: messages.issueArchivalSuccessFlagActionViewIssue,
					href: `/browse/${issueKey}`,
				},
				{
					content: fg('jira-issue-terminology-refresh-m3')
						? messages.issueArchivalSuccessFlagActionViewInArchiveIssueTermRefresh
						: messages.issueArchivalSuccessFlagActionViewInArchive,
					href: archiveIssueUrl,
				},
			],
		});
		onIssueDeleteSuccess?.({
			issueId,
			issueKey,
		});
	};

	const onArchive = () => {
		if (fg('relay-migration-archival')) {
			archiveIssueWithRelay(issueId, onArchivalSuccess, onArchivalFailure);
		} else {
			archiveIssue([issueKey], onArchivalSuccess, onArchivalFailure);
		}
		onClose?.();
		triggerCloseDeleteIssueModal();
	};

	const getChildConditionalDeleteWarning = () => {
		const deleteWithChildrenWarningMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.deleteOrArchiveWithChildrenWarningIssueTermRefresh
			: messages.deleteOrArchiveWithChildrenWarning;

		const deleteWithSubtasksWarningMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.deleteOrArchiveWithSubtasksWarningIssueTermRefresh
			: messages.deleteOrArchiveWithSubtasksWarning;

		if (isTwoStepIssueDeleteFreemiumExperimentEnabled) {
			if (fg('project-term-refresh_ga')) {
				return messages.deleteWarningForTwoStepDeleteFreemiumExperimentGalaxia;
			}
			return messages.deleteWarningForTwoStepDeleteFreemiumExperiment;
		}

		if (shouldShowChildSpecificCopy) {
			return isSimplifiedProject
				? deleteWithChildrenWarningMessage
				: deleteWithSubtasksWarningMessage;
		}
		return fg('jira-issue-terminology-refresh-m3')
			? messages.deleteOrArchiveWarningIssueTermRefresh
			: messages.deleteOrArchiveWarning;
	};

	const archiveButton = (
		<Button
			appearance="subtle"
			onClick={onArchive}
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.foundation.issue-actions.delete-issue.archive-button"
			// @ts-expect-error - TS2322 - Type '{ children: string; appearance: "subtle"; onClick: (event: SyntheticEvent<Element, Event>, analyticsEvent: UIAnalyticsEventInterface) => void; testId: string; disabled: boolean; }' is not assignable to type 'IntrinsicAttributes & ButtonProps & RefAttributes<HTMLElement>'.
			disabled={isDeletingIssue}
			key="archiveButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.archive-button.button"
		>
			{formatMessage(messages.archive)}
		</Button>
	);

	const cancelButton = (
		<Button
			appearance="subtle"
			onClick={onCancel}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="issue-view-foundation.issue-actions.delete-issue.cancel-button"
			isDisabled={isDeletingIssue}
			key="cancelButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.cancel-button.button"
		>
			{formatMessage(messages.cancel)}
		</Button>
	);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setInputValue(value);
	};

	const deleteButton = (
		<Button
			appearance={
				inputValue.trim().toLowerCase() !==
				formatMessage(messages.deleteTextToEquateForTextbox).toLowerCase()
					? 'primary'
					: 'danger'
			}
			isDisabled={
				inputValue.trim().toLowerCase() !==
					formatMessage(messages.deleteTextToEquateForTextbox).toLowerCase() || isDeletingIssue
			}
			onClick={onConfirmDeleteIssue}
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.views.issue-base.foundation.issue-actions.delete-issue.confirm-button"
			disabled={isDeletingIssue}
			// @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'ReactChild | undefined'.
			iconAfter={isDeletingIssue ? <Spinner size="small" /> : null}
			key="deleteButton"
			interactionName="auto-generated.issue.views.issue-base.foundation.issue-actions.delete-issue.confirm-button.button1"
		>
			{getRetentionDeleteButtonText() || formatMessage(messages.delete)}
		</Button>
	);

	if (!isEligibleForIssueArchival && !isTwoStepIssueDeleteFreemiumExperimentEnabled) {
		return (
			<DeleteIssueViewWithErrorBoundaryOld
				issueId={issueId}
				issueAri={issueAri}
				isDeleteIssueModalOpen={isDeleteIssueModalOpen}
				isDeletingIssue={isDeletingIssue}
				issueKey={issueKey}
				isSimplifiedProject={isSimplifiedProject}
				shouldShowChildSpecificCopy={shouldShowChildSpecificCopy}
				didIssueDeleteFail={didIssueDeleteFail}
				onReduxDeleteSuccess={onReduxDeleteSuccess}
				onReduxDeleteFailure={onReduxDeleteFailure}
				triggerDeleteRequest={triggerDeleteRequest}
				triggerCloseDeleteIssueModal={triggerCloseDeleteIssueModal}
				deleteCancelAnalytics={deleteCancelAnalytics}
				deleteConfirmAnalytics={deleteConfirmAnalytics}
				onIssueDeleteSuccess={onIssueDeleteSuccess}
				onIssueDeleteFailure={onIssueDeleteFailure}
				onClose={onClose}
			/>
		);
	}

	if (isTwoStepIssueDeleteFreemiumExperimentEnabled) {
		return (
			<>
				<ViewTracker />
				<ShortcutScope>
					<ModalDialog
						// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
						messageId="issue-view-foundation.issue-actions.delete-issue.modal-dialog.two-step-delete"
						messageType="transactional"
						width="small"
						onClose={onCancel}
					>
						<ModalHeader hasCloseButton={fg('issue-view-enghealth-40720-modal-close-button')}>
							<Grid gap="space.250" templateAreas={['title close']} xcss={gridStyles}>
								<Flex xcss={titleContainerStyles} justifyContent="start">
									<ModalTitle appearance="danger">
										{getRetentionDeleteHeading(true) ||
											formatMessage(messages.deleteHeadingWithWorkType, {
												workType,
												issueKey,
											})}
									</ModalTitle>
								</Flex>
							</Grid>
						</ModalHeader>
						<ModalBody>
							<Text as="p">
								{getRetentionDeleteWarning(workType) ||
									formatMessage(getChildConditionalDeleteWarning(), { workType })}
							</Text>
							<Box paddingBlockStart="space.150">
								<Text as="p">
									{formatMessage(
										fg('project-term-refresh_ga')
											? messages.deleteConfirmationForTwoStepDeleteFreemiumExperimentGalaxia
											: messages.deleteConfirmationForTwoStepDeleteFreemiumExperiment,
										{ workType },
									)}
								</Text>
							</Box>
							<Label htmlFor="Textfield">
								<br />
								<div css={SpanStyle}>
									{formatMessage(messages.deleteTextFieldLabelMessage, {
										strong: (...chunks: React.ReactNode[]) => (
											<Text size="small" as="strong">
												{chunks.join('')}
											</Text>
										),
									})}
								</div>
							</Label>
							<Textfield
								label={formatMessage(messages.deleteTextFieldLabelMessage)}
								value={inputValue}
								onChange={handleInputChange}
								width="352"
								height="36"
								id="Textfield"
								testId={DELETE_ISSUE_TEXTFIELD_TEST_ID}
							/>
						</ModalBody>

						<ModalFooter>
							{cancelButton}
							{deleteButton}
						</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
				<MountEvent
					onMount={() => {
						sendExperienceAnalytics({
							experience: 'issueViewDeleteIssueModalRender',
							wasExperienceSuccesful: true,
							analyticsSource: 'issueViewIssueActionsDeleteIssue',
							application: application ?? null,
							edition: edition ?? null,
						});
					}}
				/>
			</>
		);
	}

	return (
		<>
			<ViewTracker />
			<ShortcutScope>
				<ModalDialog
					// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
					messageId="issue-view-foundation.issue-actions.delete-issue.modal-dialog.new"
					messageType="transactional"
					width="small"
					onClose={onCancel}
				>
					<ModalHeader hasCloseButton={fg('issue-view-enghealth-40720-modal-close-button')}>
						<Grid gap="space.250" templateAreas={['title close']} xcss={gridStyles}>
							<Flex xcss={titleContainerStyles} justifyContent="start">
								<ModalTitle appearance="danger">
									{getRetentionDeleteHeading(true) ||
										formatMessage(messages.deleteOrArchiveHeading, {
											issueKey,
										})}
								</ModalTitle>
							</Flex>
							<Flex xcss={closeContainerStyles} justifyContent="end">
								<IconButton
									appearance="subtle"
									icon={CrossIcon}
									label={formatMessage(messages.iconButtonLabel)}
									onClick={onCancel}
								/>
							</Flex>
						</Grid>
					</ModalHeader>
					<ModalBody>
						<Text as="p">
							{getRetentionDeleteWarning(workType) ||
								formatMessage(getChildConditionalDeleteWarning())}
						</Text>
						<Label htmlFor="Textfield">
							<br />
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<span css={SpanStyle}>
								{formatMessage(messages.deleteTextFieldLabelMessage, {
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									strong: (...chunks: any[]) => (
										<Text size="small" as="strong">
											{chunks.join('')}
										</Text>
									),
								})}
							</span>
						</Label>
						<Textfield
							label={formatMessage(messages.deleteTextFieldLabelMessage)}
							value={inputValue}
							onChange={handleInputChange}
							width="352"
							height="36"
							testId={DELETE_ISSUE_TEXTFIELD_TEST_ID}
						/>
					</ModalBody>

					<ModalFooter>
						{archiveButton}
						{deleteButton}
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
			<MountEvent
				onMount={() => {
					sendExperienceAnalytics({
						experience: 'issueViewDeleteIssueModalRender',
						wasExperienceSuccesful: true,
						analyticsSource: 'issueViewIssueActionsDeleteIssue',
						application: application ?? null,
						edition: edition ?? null,
					});
				}}
			/>
		</>
	);
});
export const DeleteIssueView = AnalyticsSource(
	'issueDelete',
	MODAL,
)((props: DeleteIssueViewProps) => {
	const { issueKey } = props;
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);

	return (
		<ErrorBoundary
			id="issue.issue-view-foundation.issue-actions.delete-issue"
			onError={(location: string, error: Error, attributes: ExtraErrorAttributes) => {
				const { componentStack } = attributes;

				sendExperienceAnalytics({
					experience: 'issueViewDeleteIssueModalRender',
					wasExperienceSuccesful: false,
					analyticsSource: 'issueViewIssueActionsDeleteIssue',
					application: application ?? null,
					edition: edition ?? null,
					additionalAttributes: {
						errorMessage: error?.message || 'UNKNOWN_ERROR',
						location,
						...(componentStack && { componentStack }),
					},
				});
			}}
		>
			<DeleteIssueViewWithErrorBoundaryNew {...props} />
		</ErrorBoundary>
	);
});

export default DeleteIssueView;

const SpanStyle = css({
	fontWeight: token('font.weight.regular'),
});

const closeContainerStyles = xcss({
	gridArea: 'close',
});

const titleContainerStyles = xcss({
	gridArea: 'title',
});

const gridStyles = xcss({
	width: '100%',
});
